import { useAtomValue } from 'jotai'
import { RESET } from 'jotai/utils'
import { useSearchParams } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useEffect } from 'react'

import { showNotification } from '@/components/Notification'
import { isAuthenticatedAtom } from '@/global-state'
import { getStore } from '@/store'

import { GENERATOR_SEARCH_PARAM } from '../../generatorTypes'
import {
	draftPasswordAtom,
	passwordAtom,
	passwordEnabledAtom,
	passwordLoadedForTargetAtom,
	passwordRepeatAtom,
	savedPasswordAtom,
} from './passwordDesignState'
import { loadDraftQrPassword } from './qrPasswordDraft'
import { loadSavedQrPassword } from './qrPasswordUtils'
import { useAutosaveQrPassword } from './useAutosaveQrPassword'

export const useLoadSavedQrPassword = () => {
	const t = useTranslations()
	const isAuthenticated = useAtomValue(isAuthenticatedAtom)
	const searchParams = useSearchParams()
	// Edit mode saves to the live code, create mode to the draft
	const targetId = searchParams.get(GENERATOR_SEARCH_PARAM.Target)
	const draftId = searchParams.get(GENERATOR_SEARCH_PARAM.Id)
	const qrId = targetId ?? draftId

	useEffect(() => {
		const store = getStore()
		const loadedDraft = store.get(draftPasswordAtom)
		if (
			!qrId ||
			!draftId ||
			// On a hard reload the user is hydrated after the first render, which
			// would take a logged-in user for a guest; a re-render follows
			store.get(isAuthenticatedAtom) !== isAuthenticated ||
			(store.get(passwordLoadedForTargetAtom) === qrId &&
				loadedDraft?.qrId === draftId &&
				loadedDraft.isAuthenticated === isAuthenticated)
		) {
			return
		}
		let isCancelled = false
		const enabledBefore = store.get(passwordEnabledAtom)
		const passwordBefore = store.get(passwordAtom)
		const repeatBefore = store.get(passwordRepeatAtom)
		const isFormUntouched = () =>
			store.get(passwordEnabledAtom) === enabledBefore &&
			store.get(passwordAtom) === passwordBefore &&
			store.get(passwordRepeatAtom) === repeatBefore

		// The form follows the draft, which autosaves like the other design
		// choices. In edit mode the live code only supplies the baseline that
		// publishing compares against.
		Promise.all([
			loadDraftQrPassword(draftId, isAuthenticated),
			targetId && isAuthenticated
				? loadSavedQrPassword(targetId)
				: undefined,
		])
			.then(([draftPassword, livePassword]) => {
				if (isCancelled) {
					return
				}
				store.set(
					savedPasswordAtom,
					livePassword === undefined ? draftPassword : livePassword,
				)
				store.set(passwordLoadedForTargetAtom, qrId)
				store.set(draftPasswordAtom, {
					qrId: draftId,
					isAuthenticated,
					password: draftPassword,
				})
				if (!isFormUntouched()) {
					return
				}
				if (draftPassword === null) {
					store.set(passwordEnabledAtom, RESET)
					store.set(passwordAtom, RESET)
					store.set(passwordRepeatAtom, RESET)
				} else {
					store.set(passwordEnabledAtom, true)
					store.set(passwordAtom, draftPassword)
					store.set(passwordRepeatAtom, draftPassword)
				}
			})
			.catch((error) => {
				if (isCancelled) {
					return
				}
				console.error('Loading QR password failed', error)
				void showNotification(t('public__generic__unexpected_error'), {
					type: 'error',
				})
			})

		return () => {
			isCancelled = true
		}
	}, [qrId, draftId, targetId, isAuthenticated, t])

	useAutosaveQrPassword()
}
